import request from '../../utils/request';

// ─── Types ───

export interface CheckInScheduleView {
  id: number;
  frequency: CheckInFrequency;
  preferredDay: string | null;
  preferredDayOfMonth: number | null;
  defaultDurationMinutes: number;
  isActive: boolean;
  managerId: string;
  managerName: string;
  promptTemplates?: string | null;
}

export interface CheckInView {
  id: number;
  scheduleId: number;
  managerId: string;
  managerName: string;
  employeeId: string;
  employeeName: string;
  scheduledDate: string;
  status: CheckInStatus;
  actualDurationMinutes: number | null;
  completedAt: string | null;
  managerNotes: string | null;
  employeeNotes: string | null;
  preparationStatus: PreparationStatus;
}

export interface CheckInListView {
  totalCount: number;
  items: CheckInView[];
}

export interface CheckInSummaryView {
  totalScheduled: number;
  totalCompleted: number;
  totalMissed: number;
  upcomingCount: number;
}

export interface CreateCheckInScheduleInput {
  frequency: CheckInFrequency;
  preferredDay?: string | null;
  preferredDayOfMonth?: number | null;
  defaultDurationMinutes: number;
  promptTemplates?: string | null;
}

export interface UpdateCheckInScheduleInput {
  id: number;
  frequency: CheckInFrequency;
  preferredDay?: string | null;
  preferredDayOfMonth?: number | null;
  defaultDurationMinutes: number;
  isActive: boolean;
  promptTemplates?: string | null;
}

export interface CheckInListQuery {
  employeeId?: string;
  statusFilter?: string;
  dateFrom?: string;
  dateTo?: string;
  pageNumber?: number;
  pageSize?: number;
}

export enum CheckInFrequency {
  Weekly = 'Weekly',
  BiWeekly = 'BiWeekly',
  Monthly = 'Monthly',
}

export enum CheckInStatus {
  Scheduled = 'Scheduled',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Missed = 'Missed',
}

export enum PreparationStatus {
  NotPrepared = 'NotPrepared',
  Prepared = 'Prepared',
}

// Agenda types
export interface AutoAgendaItemView {
  id: string;
  type: string;
  title: string;
  progress?: number;
  from?: string;
  source: string;
}

export interface CustomAgendaItemView {
  id: number;
  title: string;
  notes?: string;
  sortOrder: number;
}

export interface CheckInAgendaView {
  autoItems: AutoAgendaItemView[];
  customItems: CustomAgendaItemView[];
  whatsBlocked?: string;
  whatsNeedsSupport?: string;
  prepNotes?: string;
  preparationStatus: PreparationStatus;
}

export interface SaveCheckInAgendaInput {
  checkInId: number;
  customItems: CustomAgendaItemInput[];
  whatsBlocked?: string;
  whatsNeedsSupport?: string;
  prepNotes?: string;
}

export interface CustomAgendaItemInput {
  id?: number;
  title: string;
  notes?: string;
  sortOrder: number;
}

export interface UpdatePreparationInput {
  checkInId: number;
  preparationStatus: PreparationStatus;
}

// Team readiness
export interface TeamReadinessView {
  totalUpcoming: number;
  preparedCount: number;
  members: TeamMemberReadinessView[];
}

export interface TeamMemberReadinessView {
  employeeId: string;
  employeeName: string;
  preparationStatus: PreparationStatus;
  scheduledDate: string;
}

// Continuity preview
export interface ContinuityPreviewView {
  lastCheckInDate?: string;
  lastManagerNotes?: string;
  pendingActionItems: string[];
  lastWhatsBlocked?: string;
}

// Action Items (Story 7.3)
export interface ActionItemView {
  id: number;
  checkInId: number;
  title: string;
  assigneeId?: string;
  assigneeName?: string;
  dueDate?: string;
  status: ActionItemStatus;
  completedAt?: string;
  sortOrder: number;
  carriedForward: boolean;
  sourceCheckInId?: number;
  isDevelopmentRelated: boolean;
}

export interface ActionItemListView {
  items: ActionItemView[];
}

export interface SaveActionItemsInput {
  checkInId: number;
  items: ActionItemInput[];
}

export interface ActionItemInput {
  id?: number;
  title: string;
  assigneeId?: string;
  dueDate?: string;
  status: ActionItemStatus;
  sortOrder: number;
  isDevelopmentRelated: boolean;
}

export interface UpdateActionItemStatusInput {
  actionItemId: number;
  status: ActionItemStatus;
}

export interface CompleteCheckInInput {
  checkInId: number;
  actualDurationMinutes?: number;
  managerNotes?: string;
}

export interface EmployeeCheckInHistoryQuery {
  employeeId: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface CheckInHistoryView {
  totalCount: number;
  items: CheckInHistoryItemView[];
}

export interface CheckInHistoryItemView {
  id: number;
  scheduledDate: string;
  status: CheckInStatus;
  actualDurationMinutes?: number;
  completedAt?: string;
  managerId: string;
  managerName: string;
  employeeId: string;
  employeeName: string;
  actionItemCount: number;
  actionItemCompletedCount: number;
  managerNotes?: string;
}

export enum ActionItemStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
}

export interface Result<T> {
  payload: T;
  status: number;
  errors: { code: string; message: string }[];
}

// ─── API Functions ───

export async function getSchedule(): Promise<Result<CheckInScheduleView | null>> {
  const response = await request.get<Result<CheckInScheduleView | null>>('CheckIn/GetSchedule');
  return response.data;
}

export async function createSchedule(input: CreateCheckInScheduleInput): Promise<Result<CheckInScheduleView>> {
  const response = await request.post<Result<CheckInScheduleView>>('CheckIn/CreateSchedule', input);
  return response.data;
}

export async function updateSchedule(input: UpdateCheckInScheduleInput): Promise<Result<CheckInScheduleView>> {
  const response = await request.put<Result<CheckInScheduleView>>('CheckIn/UpdateSchedule', input);
  return response.data;
}

export async function getMyCheckIns(query: CheckInListQuery = {}): Promise<Result<CheckInListView>> {
  const response = await request.get<Result<CheckInListView>>('CheckIn/GetMyCheckIns', { params: query });
  return response.data;
}

export async function getTeamCheckIns(query: CheckInListQuery = {}): Promise<Result<CheckInListView>> {
  const response = await request.get<Result<CheckInListView>>('CheckIn/GetTeamCheckIns', { params: query });
  return response.data;
}

export async function getSummary(viewMode?: string): Promise<Result<CheckInSummaryView>> {
  const response = await request.get<Result<CheckInSummaryView>>('CheckIn/GetSummary', {
    params: viewMode ? { viewMode } : undefined,
  });
  return response.data;
}

// Agenda & Preparation (Story 7.2)

export async function getCheckInAgenda(checkInId: number): Promise<Result<CheckInAgendaView>> {
  const response = await request.get<Result<CheckInAgendaView>>('CheckIn/GetCheckInAgenda', { params: { checkInId } });
  return response.data;
}

export async function saveCheckInAgenda(input: SaveCheckInAgendaInput): Promise<Result<CheckInAgendaView>> {
  const response = await request.post<Result<CheckInAgendaView>>('CheckIn/SaveCheckInAgenda', input);
  return response.data;
}

export async function updatePreparationStatus(input: UpdatePreparationInput): Promise<Result<boolean>> {
  const response = await request.put<Result<boolean>>('CheckIn/UpdatePreparationStatus', input);
  return response.data;
}

export async function getTeamReadiness(): Promise<Result<TeamReadinessView>> {
  const response = await request.get<Result<TeamReadinessView>>('CheckIn/GetTeamReadiness');
  return response.data;
}

export async function getContinuityPreview(checkInId: number): Promise<Result<ContinuityPreviewView>> {
  const response = await request.get<Result<ContinuityPreviewView>>('CheckIn/GetContinuityPreview', { params: { checkInId } });
  return response.data;
}

// Action Items, Completion & History (Story 7.3)

export async function getActionItems(checkInId: number): Promise<Result<ActionItemListView>> {
  const response = await request.get<Result<ActionItemListView>>('CheckIn/GetActionItems', { params: { checkInId } });
  return response.data;
}

export async function saveActionItems(input: SaveActionItemsInput): Promise<Result<ActionItemListView>> {
  const response = await request.post<Result<ActionItemListView>>('CheckIn/SaveActionItems', input);
  return response.data;
}

export async function updateActionItemStatus(input: UpdateActionItemStatusInput): Promise<Result<ActionItemView>> {
  const response = await request.put<Result<ActionItemView>>('CheckIn/UpdateActionItemStatus', input);
  return response.data;
}

export async function completeCheckIn(input: CompleteCheckInInput): Promise<Result<CheckInView>> {
  const response = await request.post<Result<CheckInView>>('CheckIn/CompleteCheckIn', input);
  return response.data;
}

export async function getEmployeeCheckInHistory(query: EmployeeCheckInHistoryQuery): Promise<Result<CheckInHistoryView>> {
  const response = await request.get<Result<CheckInHistoryView>>('CheckIn/GetEmployeeCheckInHistory', { params: query });
  return response.data;
}
